import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Formik, Field, FieldArray } from 'formik'
import dotty from 'dotty'

import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import FormHelperText from '@material-ui/core/FormHelperText'
import Divider from '@material-ui/core/Divider'

import utils from './utils'
import Validate from './validate'

import List from './List'

const useStyles = makeStyles(theme => ({
  errorContainer: {
    marginTop: theme.spacing(2),
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '1em',
  },
  divider: {
    marginTop: '20px',
    marginBottom: '20px',
  },
  spacingContainer: {
    marginTop: theme.spacing(3),
  }
}))

const FormWrapperInner = ({
  schema,
  initialValues,
  spacing,
  storeActions,
  renderButtons,
  renderLayout,
  onSubmit,
  error,
  validate,
}) => {

  const classes = useStyles()

  const [ hasSubmitted, setHasSubmitted ] = useState(false)

  let isInitialValid = false

  const validationSchema = Validate(schema)

  const useInitialValues = utils.getInitialValues(schema, initialValues)

  try {
    isInitialValid = validationSchema.validateSync(useInitialValues)
  } catch(e) {

  }

  const getItem = (item, formProps) => {

    if(typeof(item) == 'string') {
      return (
        <Typography
          variant='subtitle1'
        >
          { item }
        </Typography>
      )
    }
    else if (item.constructor === Array) {
      return (
        <Grid container spacing={ spacing || 2 }>
          {
            item.map((child, j) => getRow(child, formProps, j))
          }
        </Grid>
      )
    }

    if(React.isValidElement(item)) {
      return item
    }

    const error = dotty.get(formProps.errors, item.id)
    const touched = dotty.get(formProps.touched, item.id)

    return item.list ? (
      <FieldArray
        name={ item.id }
        render={arrayHelpers => (
          <List
            item={ item }
            formProps={ formProps }
            arrayHelpers={ arrayHelpers }
            storeActions={ storeActions }
          />
        )}
      />
    ) : (
      <Field
        name={ item.id }
        component={ utils.getComponent(item.component) }
        item={ item }
        error={ error }
        touched={ touched }
      />
    )
  }


  // leave up to the user to put fields into columns
  // that divide nicely into 12
  const getRow = (row, formProps, i) => {
    if(!row) return null
    if(typeof(row) === 'string') {
      return (
        <Grid item xs={ 12 } key={ i }>
          <Divider className={ classes.divider } />

          {
            row !== '-' && (
              <Typography
                variant='subtitle1'
              >
                <strong>{ row }</strong>
              </Typography>
            )
          }
          
        </Grid>
      )
    }
    else if (row.constructor === Array) {
      const colSize = Math.floor(12 / row.length)
      return row.map((item, i) => (
        <Grid item xs={ 12 } sm={ colSize } key={ i }>
          { getItem(item, formProps, i) }
        </Grid>
      ))
    }
    else {
      return (
        <Grid item xs={12} key={ i }>
          { getItem(row, formProps) }
        </Grid>
      )
    }
  }

  return (
    <Formik
      initialValues={ useInitialValues }
      validationSchema={ validationSchema }
      isInitialValid={ isInitialValid }
      validate={ validate }
      onSubmit={ (values) => {
        const processedValues = utils.processValues(schema, values)
        onSubmit(processedValues)
      }}
    >
      {
        ({
          handleSubmit,
          isValid,
          values,
          errors,
          touched,
        }) => {

          const formProps = {
            errors,
            touched,
            values,
          }

          const submitWrapper = () => {
            setHasSubmitted(true)
            handleSubmit()
          }

          const flatErrors = utils.flattenErrors(errors, utils.flattenSchema(schema))

          const buttons = renderButtons && renderButtons({
            isValid,
            values,
            handleSubmit: submitWrapper,
            errors,
          })

          const error = hasSubmitted && Object.keys(errors).length > 0 && (
            <div className={ classes.errorContainer }>
              <Typography className={ classes.errorText }>
                There are errors in the form:
              </Typography>
              <ul className={ classes.errorText }>
                {
                  Object.keys(flatErrors).map((key, i) => (
                    <li key={ i }>
                      <Typography className={ classes.errorText }>
                        { key }: { flatErrors[key] }
                      </Typography>
                    </li>
                  ))
                }
              </ul>
            </div> 
          )

          const formContent = (
            <form onSubmit={ submitWrapper }>
              <Grid container spacing={ spacing || 2 }>
                {
                  schema.map((item, i) => getRow(item, formProps, i))
                }
              </Grid>
              {
                !renderLayout && (
                  <div className={ classes.spacingContainer }>
                    { buttons }
                  </div>
                )
              }
              {
                !renderLayout && (
                  <div className={ classes.spacingContainer }>
                    { error }
                  </div>
                )
              }
            </form>
          )

          return renderLayout ?
            renderLayout({
              formContent,
              buttons,
              error,
            }) :
            formContent
        }
      }
    </Formik>
  )

}

export default FormWrapperInner
